/**
 * ProvidersAdminPanel.js — Lets the CEO create payment_agents (Cuba) and
 * ticket_providers from the same UI used to manage gestores.
 *
 * Mounted as a section under the Gestores table in /gestores.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, UserCheck, Key, Truck, Ticket, Loader2, Pencil, Trash2, Unlock } from 'lucide-react';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'payment_agent',   label: 'Proveedor de monedas (CU/UY)', icon: Truck,  hint: 'Recibe remesas y entrega CUP/MLC/USD al beneficiario.' },
  { value: 'ticket_provider', label: 'Proveedor de Tickets',           icon: Ticket, hint: 'Carga inventario de pasajes y procesa órdenes.' },
];

const COUNTRY_OPTIONS = ['CU', 'UY', 'AR', 'BR', 'CL', 'ES', 'US', 'MX'];

const emptyForm = () => ({
  role: 'payment_agent',
  name: '',
  email: '',
  phone: '',
  country: 'CU',
  password: '',
});

const ProvidersAdminPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', country: 'CU' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/providers`, { headers: getAuthHeaders() });
      setProviders(r.data.providers || []);
    } catch {
      toast.error('No se pudieron cargar los proveedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (form.role !== 'payment_agent') delete payload.country;
      await axios.post(`${API}/providers`, payload, { headers: getAuthHeaders() });
      toast.success(`${form.role === 'payment_agent' ? 'Proveedor de monedas' : 'Proveedor de tickets'} creado`);
      setDialogOpen(false);
      setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo crear el proveedor'));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (!resetTarget || !newPass) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API}/providers/${resetTarget.id}`,
        { password: newPass },
        { headers: getAuthHeaders() },
      );
      toast.success(`Password reseteado para ${resetTarget.name}. El proveedor lo cambiará al iniciar sesión.`);
      setResetTarget(null);
      setNewPass('');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo resetear el password'));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (p) => {
    const newStatus = p.status === 'active' ? 'suspended' : 'active';
    try {
      await axios.patch(`${API}/providers/${p.id}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Proveedor ${newStatus === 'active' ? 'activado' : 'suspendido'}`);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo cambiar el estado'));
    }
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setEditForm({
      name: p.name || '',
      email: p.email || '',
      phone: p.phone || '',
      country: p.country || 'CU',
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setBusy(true);
    try {
      const payload = { name: editForm.name, email: editForm.email, phone: editForm.phone };
      if (editTarget.role === 'payment_agent') payload.country = editForm.country;
      await axios.patch(`${API}/providers/${editTarget.id}`, payload, { headers: getAuthHeaders() });
      toast.success('Proveedor actualizado');
      setEditTarget(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo actualizar el proveedor'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/providers/${deleteTarget.id}`, { headers: getAuthHeaders() });
      toast.success(`Proveedor ${deleteTarget.name} eliminado`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo eliminar el proveedor'));
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (p) => {
    try {
      const r = await axios.post(`${API}/providers/${p.id}/unlock`, {}, { headers: getAuthHeaders() });
      if (r.data?.had_lockout) {
        toast.success(`Acceso desbloqueado para ${p.name}. Ya puede iniciar sesión.`);
      } else {
        toast.info(`${p.name} no tenía bloqueo activo.`);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo desbloquear'));
    }
  };

  return (
    <Card className="shadow-sm" data-testid="providers-admin-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-purple-600" /> Proveedores
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Crea y administra proveedores de monedas (CU) y de tickets desde acá mismo.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-provider-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Proveedor
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : providers.length === 0 ? (
          <p className="text-sm text-slate-400 py-10 text-center">Aún no creaste proveedores. Apretá "Nuevo Proveedor".</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Nombre</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>País</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {providers.map((p) => {
                const Icon = p.role === 'payment_agent' ? Truck : Ticket;
                return (
                  <TableRow key={p.id} data-testid={`provider-row-${p.id.slice(0, 8)}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1">
                        <Icon className="w-3 h-3" />
                        {p.role === 'payment_agent' ? 'Monedas' : 'Tickets'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{p.email}</TableCell>
                    <TableCell>{p.country || '—'}</TableCell>
                    <TableCell>
                      <Badge className={p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}>
                        {p.status === 'active' ? 'Activo' : 'Suspendido'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} title="Editar datos" data-testid={`edit-provider-${p.id.slice(0, 8)}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setResetTarget(p)} title="Resetear contraseña" data-testid={`reset-pw-${p.id.slice(0, 8)}`}>
                          <Key className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => unlock(p)} title="Desbloquear acceso (limpia intentos fallidos)" data-testid={`unlock-provider-${p.id.slice(0, 8)}`}>
                          <Unlock className="w-3.5 h-3.5 text-amber-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleStatus(p)} data-testid={`toggle-status-${p.id.slice(0, 8)}`}>
                          {p.status === 'active' ? 'Suspender' : 'Activar'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(p)} title="Eliminar" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-provider-${p.id.slice(0, 8)}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear proveedor</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="provider-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">
                {ROLE_OPTIONS.find(r => r.value === form.role)?.hint}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Nombre completo *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="provider-name-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="provider-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+53 5555 5555" data-testid="provider-phone-input" />
              </div>
              {form.role === 'payment_agent' && (
                <div className="space-y-2">
                  <Label>País *</Label>
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger data-testid="provider-country-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2 col-span-2">
                <Label>Contraseña temporal *</Label>
                <Input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="provider-password-input"
                />
                <p className="text-[10px] text-slate-400">El proveedor deberá cambiarla en su primer login.</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={busy} className="bg-purple-600 hover:bg-purple-700" data-testid="provider-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear proveedor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Resetear contraseña</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Resetear password de <strong>{resetTarget?.name}</strong>. El proveedor deberá cambiarla al iniciar sesión.
          </p>
          <Input
            type="text"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            placeholder="Nueva contraseña temporal"
            data-testid="reset-password-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancelar</Button>
            <Button onClick={resetPassword} disabled={busy || newPass.length < 6} className="bg-blue-600 hover:bg-blue-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resetear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar proveedor</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                data-testid="edit-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
                data-testid="edit-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+53 5555 5555"
                data-testid="edit-phone-input"
              />
            </div>
            {editTarget?.role === 'payment_agent' && (
              <div className="space-y-2">
                <Label>País</Label>
                <Select value={editForm.country} onValueChange={(v) => setEditForm({ ...editForm, country: v })}>
                  <SelectTrigger data-testid="edit-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-[10px] text-slate-400">
              Para cambiar la contraseña usá el botón 🔑 desde la tabla.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button>
              <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar cambios'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Eliminar proveedor</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            ¿Seguro que querés eliminar a <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})? Esta acción no se puede deshacer.
            Si el proveedor tiene operaciones activas, el sistema lo bloqueará.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button onClick={confirmDelete} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar definitivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProvidersAdminPanel;
